import {
  Component,
  Input,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl } from '@angular/forms';

import { getColorNameFromHex, SelectOption } from '../../interfaces/field-config.interface';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { FieldRendererUserComponent } from '../field-renderer-user/field-renderer-user.component';

@Component({
  selector: 'app-group-renderer-user-fields',
  standalone: true,
  imports: [
    CommonModule,
    FormSectionComponent,
    FieldRendererUserComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (group && form) {
      <app-form-section
        [title]="group.name"
        [icon]="groupIcon"
        [iconColor]="groupIconColor"
        class="mb-6">

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

          @for (field of visibleFields; track trackByFieldId($index, field)) {

            @if (getControl(getFieldName(field)); as control) {

              <div 
                [class]="isFullWidth(field) ? 'md:col-span-2' : ''">

                <app-field-renderer-user
                  [config]="adaptField(field)"
                  [control]="control"
                  [externalOptions]="getExternalOptions(getFieldName(field))">
                </app-field-renderer-user>

              </div>

            }

          }

        </div>

      </app-form-section>
    }
  `
})
export class GroupRendererUserFieldsComponent {

  @Input({ required: true }) group!: any;
  @Input({ required: true }) form!: FormGroup;

  @Input() externalOptionsMap: Map<string, SelectOption[]> = new Map();


  get visibleFields(): any[] {
    return this.group?.fields?.filter((field: any) => {
      const visibility =
        field.visible ??
        field.isVisible ??
        true;

      return visibility !== false;
    }) || [];
  }


  get groupIcon(): string {
    return this.group?.icon || 'folder';
  }

  get groupIconColor(): string {
    return getColorNameFromHex(this.group?.iconColor);
  }


  getFieldName(field: any): string {
    const key = field.fieldName ?? field.name;
    return typeof key === 'string' ? key.trim() : '';
  }

  isFullWidth(field: any): boolean {
    const type = field.fieldType || field.type;
    return type === 'form' || type === 'attachment';
  }

  getControl(fieldName: string): FormControl<any> | null {
    if (!this.form) return null;
    return this.form.get(fieldName) as FormControl<any> || null;
  }

  getExternalOptions(fieldName: string): SelectOption[] {
    return this.externalOptionsMap?.get(fieldName) || [];
  }

  trackByFieldId(index: number, field: any): string | number {
    const fn = typeof field.fieldName === 'string' ? field.fieldName.trim() : '';
    const nm = typeof field.name === 'string' ? field.name.trim() : '';
    return field._id || fn || nm || index;
  }

  adaptField(field: any): any {
    const canonical = field.fieldName ?? field.name;
    return {
      ...field,
      type: field.fieldType || field.type,
      name: typeof canonical === 'string' ? canonical.trim() : canonical,
      visible: field.visible ?? field.isVisible ?? true,
      label:
        field.labels?.master ||
        field.fieldLabel?.master ||
        field.name ||
        field.fieldName
    };
  }
}